'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Accordion from '@/components/ui/Accordion';
import Footer from '@/components/common/Footer';
import StickyCTA from '@/components/ui/StickyCTA';

export default function StrategySprintLander() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    // Sticky CTA on scroll
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const faqItems = [
    {
      id: 'faq-1',
      title: 'What if I already have a marketing plan?',
      content: 'Great! We\'ll review it, identify gaps, and help you prioritize execution. Most plans fail because they\'re too ambitious or lack capacity planning. We fix that.'
    },
    {
      id: 'faq-2',
      title: 'Do I need to commit to ongoing work after the sprint?',
      content: 'No. The Sprint is standalone. After delivery, you choose your path: take the roadmap and execute internally, engage us for a fixed-scope buildout ($10k–$75k+), or move into ongoing execution ($3k–$10k+/mo). There is no obligation to continue.'
    },
    {
      id: 'faq-3',
      title: 'How long does it take?',
      content: '2 weeks from kickoff to final delivery. Week 1: Discovery and strategy sessions. Week 2: Roadmap creation and presentation.'
    },
    {
      id: 'faq-4',
      title: 'What are the three paths after the sprint?',
      content: 'After the sprint you have three options: (1) Roadmap Only — take the deliverables and execute internally. (2) Project Implementation — we scope and build the highest-priority work as a fixed-fee project. (3) Ongoing Execution — we become your execution partner, shipping every week and optimizing every month.'
    }
  ];

  return (
    <>
      {/* Reduced Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <AppImage 
              src="/assets/images/boc_logo_240x90_white-1771394304209.png"
              alt="Best Odds Corp Logo"
              width={180}
              height={68}
              className="cursor-pointer"
            />
          </Link>
          <Link href="/cart">
            <Button variant="primary" size="md" className="border-glow">
              Get Started - $2,500
            </Button>
          </Link>
        </div>
      </nav>

      {/* Sticky CTA */}
      {showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border py-4 px-6">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div>
              <p className="text-h5">Strategy Sprint</p>
              <p className="text-body-sm text-muted-foreground">$2,500 one-time • 2-week engagement</p>
            </div>
            <Link href="/cart">
              <Button variant="primary" size="lg" className="border-glow">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      )}

      <StickyCTA
        title="Strategy Sprint"
        subtitle="$2,500 one-time • 2-week engagement"
        ctaText="Get Started"
        ctaHref="/cart"
        showAfterScroll={800}
      />

      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <Badge variant="primary" className="mb-6">Strategic Entry Point — 2-Week Engagement</Badge>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                Find the bottleneck.
                <br />
                <span className="text-primary">Build the roadmap first.</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
                A paid strategic diagnostic that identifies your highest-value opportunities, maps your constraints, and delivers a prioritized execution roadmap — before you commit to anything else.
              </p>
              <p className="text-body-sm text-muted-foreground max-w-2xl mx-auto mb-12">
                Typically $5k–$10k. Entry point at $2,500 for smaller businesses. $15k+ for larger or more established operations.
              </p>
              <Link href="/cart">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Book a Sprint — Start With a Diagnostic
                </Button>
              </Link>
              <p className="text-body-sm text-muted-foreground mt-4">
                No retainer required. No ongoing commitment. Just clarity and a clear path forward.
              </p>
            </div>
          </div>
        </section>

        {/* Who It's For / Not For */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="reveal p-8 border border-primary bg-surface-1">
                <h3 className="text-h3 mb-6">This is for you if:</h3>
                <ul className="space-y-4 text-body-sm">
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're spending on marketing but not sure if it's the right mix</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You have a team but no clear execution plan</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're considering hiring an agency but want to validate the investment first</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You need to prioritize 10 ideas into 3 actionable initiatives</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You want capacity planning, not just a strategy deck</span>
                  </li>
                </ul>
              </div>
              <div className="reveal p-8 border border-border bg-surface-1">
                <h3 className="text-h3 mb-6">This is NOT for you if:</h3>
                <ul className="space-y-4 text-body-sm text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You need execution right now (go straight to Execution Coverage)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You're looking for a 50-slide strategy deck to present to your board</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You want us to validate your existing plan (we'll tell you what's wrong)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You're not ready to make decisions and commit to execution</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Deliverables Checklist */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                What You Get
              </span>
              <h2 className="text-h2 mb-4">Deliverables Checklist</h2>
              <p className="text-body-sm text-muted-foreground max-w-2xl mx-auto">
                Everything you need to execute with confidence.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ 90-Day Execution Roadmap</h4>
                <p className="text-body-sm text-muted-foreground">
                  Month-by-month breakdown of what to execute, in what order, and why.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Capacity Bucket Planning</h4>
                <p className="text-body-sm text-muted-foreground">
                  How many hours you need per month, allocated across Always-On, Growth, and Projects.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Channel Strategy & Prioritization</h4>
                <p className="text-body-sm text-muted-foreground">
                  Which channels to focus on, which to test, and which to ignore (for now).
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Creative Brief Templates</h4>
                <p className="text-body-sm text-muted-foreground">
                  Ready-to-use templates for campaigns, content, and creative projects.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Metrics & Tracking Setup</h4>
                <p className="text-body-sm text-muted-foreground">
                  What to measure, how to track it, and what success looks like.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Recommended Plan & Budget</h4>
                <p className="text-body-sm text-muted-foreground">
                  Whether you need Execution Coverage, Growth Partner, AutoPilot, or a hybrid approach.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Timeline */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                How It Works
              </span>
              <h2 className="text-h2 mb-4">2-Week Process</h2>
              <p className="text-body-sm text-muted-foreground max-w-2xl mx-auto">
                Fast, focused, and actionable.
              </p>
            </div>
            <div className="space-y-8">
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">1</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Kickoff & Discovery (Days 1-3)</h4>
                  <p className="text-body-sm text-muted-foreground">
                    We audit your current marketing, review your goals, and identify gaps. 2-hour kickoff call + async questionnaire.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">2</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Strategy Development (Days 4-7)</h4>
                  <p className="text-body-sm text-muted-foreground">
                    We build your 90-day roadmap, capacity plan, and channel strategy. 1-hour mid-sprint check-in to align.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">3</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Roadmap Refinement (Days 8-10)</h4>
                  <p className="text-body-sm text-muted-foreground">
                    We finalize deliverables, create templates, and prepare your execution blueprint.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">4</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Final Presentation (Days 11-14)</h4>
                  <p className="text-body-sm text-muted-foreground">
                    90-minute presentation of your roadmap, capacity plan, and recommended next steps. You walk away ready to execute.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Proof Placeholders */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Proof
              </span>
              <h2 className="text-h2 mb-4">What Clients Say</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "We were spending $10k/mo on ads with no clear strategy. The Strategy Sprint gave us a roadmap and cut our wasted spend by 40%."
                </p>
                <p className="text-body-sm font-semibold">— Sarah K., E-commerce Brand</p>
              </div>
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "Best $2,500 we've spent. We went from 10 ideas to 3 priorities and knew exactly how much capacity we needed to execute."
                </p>
                <p className="text-body-sm font-semibold">— Mike T., SaaS Founder</p>
              </div>
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "We used the roadmap to hire our first marketing person. It gave us the clarity we needed to make the right hire."
                </p>
                <p className="text-body-sm font-semibold">— Jessica L., B2B Services</p>
              </div>
            </div>
          </div>
        </section>

        {/* Objection FAQ */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Questions?
              </span>
              <h2 className="text-h2 mb-4">Common Objections</h2>
            </div>
            <div className="max-w-3xl mx-auto reveal">
              <Accordion items={faqItems} allowMultiple={true} />
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 px-6">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Get clarity in 2 weeks.
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                $2,500 one-time. No ongoing commitment. Just a roadmap you can actually execute.
              </p>
              <Link href="/cart">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Book Your Sprint Now
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Next Steps Section */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-12">
              <h2 className="text-3xl font-bold mb-4">Three Paths After Your Sprint</h2>
              <p className="text-body text-muted-foreground">The sprint delivers clarity. Then you choose the right execution path.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="reveal p-6 border border-border bg-surface-1">
                <div className="text-primary font-bold text-sm mb-3 uppercase tracking-widest">Path 01</div>
                <h3 className="text-xl font-semibold mb-2">Roadmap Only</h3>
                <p className="text-body-sm text-muted-foreground mb-4">Take the sprint deliverables and execute internally. You own the plan.</p>
              </div>
              <Link href="/pricing" className="reveal p-6 border border-border hover:border-primary transition-colors group bg-surface-1">
                <div className="text-primary font-bold text-sm mb-3 uppercase tracking-widest">Path 02</div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">Project Implementation</h3>
                <p className="text-body-sm text-muted-foreground mb-4">We scope and build the highest-priority work as a fixed-fee project ($10k–$75k+).</p>
                <span className="text-primary text-body-sm">See buildout pricing →</span>
              </Link>
              <Link href="/pricing" className="reveal p-6 border border-border hover:border-primary transition-colors group bg-surface-1">
                <div className="text-primary font-bold text-sm mb-3 uppercase tracking-widest">Path 03</div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">Ongoing Execution</h3>
                <p className="text-body-sm text-muted-foreground mb-4">We become your execution partner — shipping every week, optimizing every month ($3k–$10k+/mo).</p>
                <span className="text-primary text-body-sm">Explore execution →</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}